import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { paceBand, type Tone } from '@/lib/plain-language'
import EngineDetails from '@/components/EngineDetails'
import { useFigureScope } from '@/lib/figure-scope'
import type { RoomPaceEntry } from '@/types/calendar'

interface RoomPaceTableProps {
  roomTypes: RoomPaceEntry[]
  property: { occupancyPct: number; baselineOccPct: number | null; paceRatio: number | null }
}

// Worded pace, never a bare multiplier. The raw ratios live in EngineDetails below.
const PACE_TEXT_TONE: Record<Tone, string> = {
  positive: 'text-emerald-600',
  neutral: 'text-muted-foreground',
  negative: 'text-rose-600',
}

function paceCell(ratio: number | null): { label: string; className: string } {
  const band = paceBand(ratio)
  return {
    label: band.key === 'unknown' ? '—' : band.label,
    className: band.key === 'unknown' ? 'text-muted-foreground' : PACE_TEXT_TONE[band.tone],
  }
}

function rawRatio(ratio: number | null): string {
  return ratio != null ? `${ratio.toFixed(2)}x` : '—'
}

export default function RoomPaceTable({ roomTypes, property }: RoomPaceTableProps) {
  // The pooled row is the whole building at a combined inventory ("Building", the server's word) and
  // the property everywhere else, as it always read.
  const pooledName = useFigureScope().pooledLabel ?? 'Property'
  const sorted = [...roomTypes].sort((a, b) => {
    if (a.paceRatio == null && b.paceRatio == null) return 0
    if (a.paceRatio == null) return 1
    if (b.paceRatio == null) return -1
    return a.paceRatio - b.paceRatio
  })

  return (
    <div className="rounded-lg border border-border/40 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="group/row">
            <TableHead className="text-xs">Room Type</TableHead>
            <TableHead className="text-xs text-right">Full now</TableHead>
            <TableHead className="text-xs text-right">Usually</TableHead>
            <TableHead className="text-xs text-right">
              <span className="inline-flex items-center gap-1">
                Pace
                {/* Raw pace ratios — icon-only affordance, one click away; the only
                    place the bare multiplier appears. */}
                <EngineDetails variant="icon">
                  {sorted.map((rt) => (
                    <EngineDetails.Row key={rt.id} label={rt.name} value={rawRatio(rt.paceRatio)} />
                  ))}
                  <EngineDetails.Row label={pooledName} value={rawRatio(property.paceRatio)} />
                </EngineDetails>
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((rt) => {
            const pace = paceCell(rt.paceRatio)
            return (
              <TableRow key={rt.id}>
                <TableCell className="text-xs font-medium">
                  {rt.name}
                  {rt.scopeLabel && (
                    <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary">
                      {rt.scopeLabel}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-right tabular-nums">{rt.occupancyPct.toFixed(0)}%</TableCell>
                <TableCell className="text-xs text-right tabular-nums text-muted-foreground">
                  {rt.baselineOccPct != null ? `~${rt.baselineOccPct.toFixed(0)}%` : '—'}
                </TableCell>
                <TableCell className={cn('text-xs text-right font-semibold', pace.className)}>
                  {pace.label}
                </TableCell>
              </TableRow>
            )
          })}
          <TableRow className="border-t-2 border-border/60">
            <TableCell className="text-xs font-bold">{pooledName}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold">{property.occupancyPct.toFixed(0)}%</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-bold text-muted-foreground">
              {property.baselineOccPct != null ? `~${property.baselineOccPct.toFixed(0)}%` : '—'}
            </TableCell>
            {(() => {
              const pace = paceCell(property.paceRatio)
              return (
                <TableCell className={cn('text-xs text-right font-bold', pace.className)}>
                  {pace.label}
                </TableCell>
              )
            })()}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
